/* global Excel, Office, clearTimeout, document, HTMLSelectElement, setInterval, setTimeout */

import { ReportingTargetFilterRegistry } from '../reporting-target/reporting-target-filter-registry';
import type { ReportingTaskPaneFilters } from '../reporting-filters/reporting-filters.controller';
import type {
    ReportingTaskPaneTokenPort,
    ReportingTaskPaneUserInfoPort,
} from '../reporting-filters/reporting-filters.ports';
import { loadTraceSheetFiltersNamedItemForActiveSheet } from './trace-sheet-filter-named-item.util';
import { applyTraceSheetFilterPersistedToDom } from './trace-sheet-filter-snapshot.util';
import { clearReportFilterFieldHighlights } from './report-filter-field-validation-highlights.util';

function debounce(fn: () => void, ms: number): () => void {
    let t: ReturnType<typeof setTimeout> | undefined;
    return () => {
        if (t !== undefined) {
            clearTimeout(t);
        }
        t = setTimeout(() => {
            t = undefined;
            fn();
        }, ms);
    };
}

function excelApiSetSupported(minVersion: string): boolean {
    try {
        return (
            typeof Office !== 'undefined' &&
            Office.context?.requirements?.isSetSupported('ExcelApi', minVersion) === true
        );
    } catch {
        return false;
    }
}

/**
 * When the user activates a worksheet, rehydrate the task pane filters from metadata saved for that sheet.
 *
 * @returns Debounced restore scheduler — call after sign-in (once facilities + panels are ready) so the
 *   active sheet is re-read when the initial run happened while disconnected.
 */
export function registerTraceSheetFilterWorksheetActivation(params: {
    readonly tokens: ReportingTaskPaneTokenPort;
    readonly userInfo: ReportingTaskPaneUserInfoPort;
    readonly reporting: ReportingTaskPaneFilters;
}): () => void {
    if (typeof Excel === 'undefined') {
        return () => {};
    }

    const registry = new ReportingTargetFilterRegistry();
    let restoring = false;
    let pollTimer: ReturnType<typeof setInterval> | undefined;
    let lastPolledSheetId: string | null = null;

    async function runRestoreFromActiveSheet(): Promise<void> {
        if (restoring) {
            return;
        }
        if (!params.tokens.hasConnectedSession()) {
            return;
        }
        if (document.getElementById('data-section')?.getAttribute('aria-busy') === 'true') {
            return;
        }

        restoring = true;
        try {
            let snapshot;
            try {
                snapshot = await loadTraceSheetFiltersNamedItemForActiveSheet();
            } catch {
                return;
            }
            if (!snapshot) {
                return;
            }

            const definition = registry.getFilterByReportingTarget(snapshot.targetId);
            if (!definition.fetchImplemented) {
                return;
            }

            const accessToken = params.tokens.getTokenData()?.accessToken;
            if (accessToken) {
                await params.userInfo.ensureFacilitiesLoaded(accessToken);
            }

            const targetSel = document.getElementById('report-target') as HTMLSelectElement | null;
            if (!targetSel) {
                return;
            }
            targetSel.value = snapshot.targetId;
            params.reporting.handleReportingTargetChange();
            applyTraceSheetFilterPersistedToDom({ snapshot, definition });
            clearReportFilterFieldHighlights();
            params.reporting.applyPersistedSheetFetchFingerprint(snapshot.fetchFingerprint);
            params.reporting.syncRefreshButtonState();
        } finally {
            restoring = false;
        }
    }

    const debouncedRestore = debounce(() => {
        void runRestoreFromActiveSheet();
    }, 150);

    function startPollingFallback(): void {
        if (pollTimer !== undefined) {
            return;
        }
        pollTimer = setInterval(() => {
            if (typeof Excel === 'undefined') {
                return;
            }
            void Excel.run(async (context) => {
                const sheet = context.workbook.worksheets.getActiveWorksheet();
                sheet.load('id');
                await context.sync();
                if (sheet.id !== lastPolledSheetId) {
                    lastPolledSheetId = sheet.id;
                    debouncedRestore();
                }
            }).catch(() => {
                /* workbook not ready */
            });
        }, 600);
    }

    void Excel.run(async (context) => {
        if (excelApiSetSupported('1.7')) {
            try {
                context.workbook.worksheets.onActivated.add(() => {
                    debouncedRestore();
                });
            } catch {
                startPollingFallback();
            }
        } else {
            startPollingFallback();
        }
        await context.sync();
    })
        .then(() => {
            debouncedRestore();
        })
        .catch(() => {
            startPollingFallback();
            debouncedRestore();
        });

    return debouncedRestore;
}
